// Configuraciones de motor: cada una dice qué motor (y retardo) lleva cada portamotor. El diseño guarda en sus tubos
// marcados los motores de la configuración activa; las demás se aplican sobre una copia al simular.
import { listMounts, setMountMotor, type Motor, type MountTube, type Rocket } from '@openrocket-web/physics';
import { findMotor } from './motors';

/** Cuándo se enciende un motor. Se guarda pero todavía no se edita ni se simula: se enciende todo en el lanzamiento. */
export interface Ignition {
  readonly event: 'stage' | 'launch' | 'delay';
  /** Retardo (s) tras el evento. */
  readonly delay: number;
}

export interface MotorAssignment {
  readonly motorId: string;
  readonly delay: number;
  /** Sin él, la ignición es automática. */
  readonly ignition?: Ignition;
}

export interface MotorConfig {
  readonly id: string;
  /** Nombre elegido por el usuario; sin él, se compone con los motores: «[C6-5]». */
  readonly name?: string;
  /** Motor de cada portamotor, por id del tubo. */
  readonly motors: Readonly<Record<string, MotorAssignment>>;
}

/** Tubos marcados como portamotor, en orden de pila. */
export function mountsOf(rocket: Rocket): MountTube[] {
  return listMounts(rocket).map((m) => m.tube);
}

/** Configuración que reproduce los motores que el diseño lleva ahora. */
export function configFromRocket(rocket: Rocket, id: string, name?: string): MotorConfig {
  const motors: Record<string, MotorAssignment> = {};
  for (const m of listMounts(rocket)) if (m.mark.motor !== undefined) motors[m.tube.id] = { motorId: m.mark.motor.motorId, delay: m.mark.motor.ejectionDelay };
  return { id, ...(name === undefined ? {} : { name }), motors };
}

/**
 * Copia del diseño con los motores de `config`. Si ya los lleva devuelve el mismo objeto, para que comparar por
 * referencia siga diciendo si el diseño cambió.
 */
export function applyConfig(rocket: Rocket, config: MotorConfig): Rocket {
  let out = rocket;
  for (const m of listMounts(rocket)) {
    const a = config.motors[m.tube.id];
    out = setMountMotor(out, m.tube.id, a === undefined ? undefined : { motorId: a.motorId, ejectionDelay: a.delay });
  }
  return out;
}

const designation = (m: Motor | undefined, a: MotorAssignment): string => (m === undefined ? a.motorId : `${m.designation}-${m.plugged ? 'P' : a.delay}`);

/** «C6-5» para un motor asignado. */
export function assignmentLabel(a: MotorAssignment, library: readonly Motor[]): string {
  return designation(findMotor(library, a.motorId), a);
}

/** Nombre que se muestra: el elegido, o «[A8-3]» (varios soportes se separan con espacio), o «[Sin motores]». */
export function configLabel(config: MotorConfig, rocket: Rocket, library: readonly Motor[]): string {
  if (config.name !== undefined && config.name !== '') return config.name;
  const parts = mountsOf(rocket)
    .map((m) => config.motors[m.id])
    .filter((a): a is MotorAssignment => a !== undefined)
    .map((a) => assignmentLabel(a, library));
  return parts.length === 0 ? '[Sin motores]' : `[${parts.join(' ')}]`;
}
